import type { RefObject } from 'react';
import { create } from 'zustand';

type FocusTarget = RefObject<HTMLElement> | HTMLElement | null;

export type ModalManagerItem = {
  id: string;
  focusTargetWhenOpened: RefObject<HTMLElement>;
  focusTargetsWhenClosed?: FocusTarget[];
};

export type ModalManagerStoreState = {
  modalStack: ModalManagerItem[];
  setModalOpened: (item: ModalManagerItem) => void;
  setModalClosed: (id: string, options?: { skipFocus?: true }) => void;

  currentFocusTargets: FocusTarget[];
  clearCurrentFocusTargets: () => void;
};

export const modalManagerStore = create<ModalManagerStoreState>(set => ({
  modalStack: [],
  setModalOpened: newItem => {
    set(state => {
      if (state.modalStack.every(existingItem => existingItem.id !== newItem.id)) {
        const activeItem = getActiveItemFromState(state);
        const focusTargetsWhenClosed = [
          document.activeElement as HTMLElement | null,
          ...(newItem.focusTargetsWhenClosed ?? []),
          activeItem?.focusTargetWhenOpened ?? null
        ];
        return {
          currentFocusTargets: [newItem.focusTargetWhenOpened],
          modalStack: [...state.modalStack, { ...newItem, focusTargetsWhenClosed }]
        };
      }
      return {};
    });
  },
  setModalClosed: (idToClose, { skipFocus } = {}) => {
    set(state => {
      const closingItem = state.modalStack.find(existingItem => existingItem.id === idToClose);
      if (closingItem) {
        const activeItem = getActiveItemFromState(state);
        let currentFocusTargets: FocusTarget[] = [];
        if (activeItem?.id === idToClose) {
          if (!skipFocus) {
            currentFocusTargets = [...(activeItem?.focusTargetsWhenClosed ?? []), document.querySelector('h1')];
          }
        } else {
          const itemAboveClosingItem = state.modalStack[state.modalStack.indexOf(closingItem) + 1];
          itemAboveClosingItem.focusTargetsWhenClosed?.push(...(closingItem.focusTargetsWhenClosed ?? []));
        }
        return {
          currentFocusTargets,
          modalStack: state.modalStack.filter(existingItem => existingItem.id !== idToClose)
        };
      }
      return {};
    });
  },

  currentFocusTargets: [],
  clearCurrentFocusTargets: () => set({ currentFocusTargets: [] })
}));

function getActiveItemFromState({ modalStack }: ModalManagerStoreState): ModalManagerItem | undefined {
  return modalStack[modalStack.length - 1];
}

export const useModalManagerStore = modalManagerStore;
